// UMI-election domain helpers + the reprice wire contract for the unleased-mineral
// election surface. Pure, dependency-free: the three-variant
// catalog (labels + colors + order), the default terms + acreage-seeded lease bonus,
// the completeness gate, the terms key, and the derivations that turn the calculator's
// per-variant series into the cumulative net-to-owner comparison lines + outcome cards.
//
// UMI is per-tract (no groups, no portfolio, no recombination). The election MATH is
// server-side — the calculator computes all three variants (carry / participate / lease)
// off the frozen frame every reprice; this module only CUMULATES the returned single-leg
// series and reshapes them for display. The trigger vocabulary is shared with carry
// (`CARRY_TRIGGERS`) — the election just applies it as a risk-penalty payout.
import {
  CARRY_TRIGGER_BY_ID,
  carryTermsFromWells,
  splitWellsByInterest,
  thresholdForTrigger,
  type CarryApoTerms,
} from './interest-modeling-carry';
import { alignCumulativeToAxis, runningSum } from './interest-modeling-preview';
import type {
  CarryTrigger,
  CarryWell,
  ElectionGroup,
  ElectionVariant,
  InterestItem,
} from './types';

export type { ElectionGroup, ElectionVariant } from './types';

// ── Wire types (interest_preview response `tract.elections`, deep-camelized) ───

/**
 * One monthly point on an election variant's series — SINGLE-LEG absolute
 * net-to-owner components (not the carry base/working 4-tuple): `return` = the
 * owner's royalty/revenue that month, `expense` = the owner's cost that month
 * (0 for lease; the up-front bonus rides month-1 `return`). Net = return − expense.
 */
export interface ElectionSeriesPoint {
  date: string;
  return: number;
  expense: number;
}

/** One election variant's reprice outcome (the calculator computes all three regardless of `electedElection`). */
export interface ElectionResult {
  election: ElectionVariant;
  /** ISO payout date, or null (never reverts — participate / lease, or a carry that never pays out). */
  payoutMonth: string | null;
  /** Undiscounted net to the owner over the horizon ($). */
  netToOwner: number;
  /** Net present value at the flat discount rate ($). */
  npv: number;
  /** Deepest capital-at-risk drawdown ($, ≤ 0; 0 when the owner never bears cost). */
  peakExposure: number;
  series: ElectionSeriesPoint[];
}

/**
 * One election GROUP's reprice payload (`response.tract.elections[]`). One item ⇒ one
 * tract, but a tract prices one entry per property group (INS-10673). `groupId` is
 * absent/null for an election saved before grouping — see {@link LEGACY_ELECTION_GROUP_ID}.
 */
export interface PreviewElection {
  tractId: string | number;
  groupId?: string | null;
  results: ElectionResult[];
}

// ── Variant catalog (labels + colors + order) ─────────────────────────────────

/** One election variant's display metadata (mock `ELECTION_LABELS` / `ELECTION_COLORS`). */
export interface ElectionVariantMeta {
  id: ElectionVariant;
  label: string;
  color: string;
}

/**
 * The three elections in display order (carry / participate / lease). Labels + colors
 * match the PM mock (`ELECTION_LABELS` / `ELECTION_COLORS`), the interaction truth.
 */
export const ELECTION_VARIANTS: ElectionVariantMeta[] = [
  { id: 'carried', label: 'Carried (non-consent)', color: '#0f62fe' },
  {
    id: 'participate',
    label: 'Participate (working interest)',
    color: '#007d79',
  },
  { id: 'lease', label: 'Sign a lease', color: '#8a3ffc' },
];

/** Variant id → its metadata. */
export const ELECTION_VARIANT_BY_ID = Object.fromEntries(
  ELECTION_VARIANTS.map((meta) => [meta.id, meta])
) as Record<ElectionVariant, ElectionVariantMeta>;

// ── Term caps + defaults (mock ElectionControls / makeElectionItem) ────────────

/** Carried royalty rate cap (% of the owner's share) — the mock's PctInput `max`. */
export const CARRIED_ROYALTY_MAX = 25;
/** Lease royalty rate cap (% of the owner's share) — the mock's PctInput `max`. */
export const LEASE_ROYALTY_MAX = 30;
/** Default $/net-mineral-acre used to seed the lease bonus from real acreage (mock `bonusPerAcre`). */
export const DEFAULT_BONUS_PER_ACRE = 3000;
/** The risk-penalty trigger a fresh election opens on (shares the carry catalog). */
export const DEFAULT_ELECTION_TRIGGER: CarryTrigger = 'moi';

/** The default participate / carried-royalty / lease-royalty terms for a fresh election (mock `makeElectionItem`). */
export const DEFAULT_ELECTION_TERMS = {
  ownerWi: 25,
  ownerNri: 20,
  carriedRoyaltyNri: 0,
  leaseRoyaltyNri: 20,
} as const;

/** The election term fields seeded onto an interest item on add (a subset of {@link InterestItem}). */
export interface ElectionSeed {
  /** Every valued well on the tract — the union of the groups' properties. */
  wellIds: string[];
  /**
   * The tract's property groups. Each prices ONE interest: its own properties and
   * the participating WI/NRI they carry (INS-10673). Same rule as an OGL carry
   * group — a tract whose properties disagree on our interest cannot be priced as
   * one position, so the user splits it into groups that can.
   */
  electionGroups: ElectionGroup[];
  carriedRoyaltyNri: number;
  trigger: CarryTrigger;
  threshold: number;
  leaseRoyaltyNri: number;
  leaseBonus: number;
}

/**
 * A fresh election property group. Participating terms are OUR interest in the
 * group's properties, so they are seeded from the properties or left blank — never
 * a preset, which would price a position nobody chose (INS-10673).
 *
 * @param {string} name - The group's display name.
 * @param {string[]} [wellIds] - The properties it starts with (default none).
 * @param {CarryApoTerms | null} [terms] - Our interest in those properties, when derivable.
 * @return {ElectionGroup} The new group.
 */
export function makeElectionGroup(
  name: string,
  wellIds: string[] = [],
  terms?: CarryApoTerms | null
): ElectionGroup {
  return {
    id: crypto.randomUUID(),
    name,
    wellIds,
    // Carried means we hold nothing before payout, so the BPO pair starts at 0 and
    // is the user's to change — not derived, and never pre-filled with a rate
    // nobody entered.
    bpoWi: 0,
    bpoNri: 0,
    ownerWi: terms ? terms.apoWi : null,
    ownerNri: terms ? terms.apoNri : null,
  };
}

/**
 * An election item's property groups. Elections predating grouping (INS-10673) hold
 * their properties and participating terms on the item itself; those are read as the
 * one implicit group they always were, so a saved eval reopens unchanged.
 *
 * @param {InterestItem} item - The interest item.
 * @return {ElectionGroup[]} The item's groups.
 */
export function electionGroupsOf(item: InterestItem): ElectionGroup[] {
  if (item.electionGroups) {
    // A group saved before the before-payout pair existed states neither. Fill it
    // with what that group actually PRICED — no working interest, and the tract's
    // royalty scaled by our share — rather than a zero that would quietly rewrite
    // the position, or a blank that would read as an unfinished group and block the
    // preview. A group that states its own pair is returned untouched.
    return item.electionGroups.map((group) => {
      if (group.bpoWi !== undefined && group.bpoNri !== undefined) {
        return group;
      }
      return {
        ...group,
        bpoWi: group.bpoWi ?? 0,
        bpoNri: group.bpoNri ?? legacyCarriedBpoNri(item) ?? 0,
      };
    });
  }
  if (item.kind !== 'election' || item.wellIds === undefined) {
    return [];
  }
  return [
    {
      id: LEGACY_ELECTION_GROUP_ID,
      name: 'Group 1',
      wellIds: item.wellIds,
      // A pre-BPO item priced its carried leg as "no WI, and the tract royalty
      // scaled by our share". Synthesize exactly that, so a reopened eval shows and
      // reprices the position it was saved with rather than a zeroed one.
      bpoWi: 0,
      // No royalty on the saved item means no royalty — 0, not "unstated", which
      // would read as an unfinished group and hold Calculate off.
      bpoNri: legacyCarriedBpoNri(item) ?? 0,
      ownerWi: item.ownerWi ?? null,
      ownerNri: item.ownerNri ?? null,
      electedElection: item.electedElection,
    },
  ];
}

/**
 * The before-payout NRI a pre-BPO election priced its carried leg at: the tract's
 * royalty rate (a percent of OUR share) scaled by our working interest, which is the
 * absolute percent of the well the calculator used. Null when either part is absent.
 *
 * @param {InterestItem} item - The saved election item.
 * @return {number | null} The equivalent before-payout NRI (% of the well).
 */
function legacyCarriedBpoNri(item: InterestItem): number | null {
  if (
    typeof item.carriedRoyaltyNri !== 'number' ||
    typeof item.ownerWi !== 'number'
  ) {
    return null;
  }
  return (item.carriedRoyaltyNri * item.ownerWi) / 100;
}

/**
 * The group id an ungrouped (pre-INS-10673) election reads as. The calculator emits
 * `groupId: null` for such an item, and {@link electionResultKey} maps both to this,
 * so a legacy election's results land where its implicit group looks for them.
 */
export const LEGACY_ELECTION_GROUP_ID = '__election__';

/**
 * The preview cache key for one group's outcomes.
 *
 * @param {string | number} tractId - The tract.
 * @param {string | null} [groupId] - The group (null/absent ⇒ the legacy implicit group).
 * @return {string} The cache key.
 */
export function electionResultKey(
  tractId: string | number,
  groupId?: string | null
): string {
  return `${tractId}:${groupId ?? LEGACY_ELECTION_GROUP_ID}`;
}

/**
 * The seeded lease bonus: `$/NMA × net acres` when real acreage resolves, else 0
 * (a manual total — NEVER a fabricated acreage figure). Mirrors the mock, which
 * seeds the lump sum from the tract's own `netAcres`.
 *
 * @param {number | null} [netAcres] - The tract's net acres (from the assets read), or null/absent.
 * @return {number} The seeded bonus total ($).
 */
export function seedLeaseBonus(netAcres?: number | null): number {
  return typeof netAcres === 'number' &&
    Number.isFinite(netAcres) &&
    netAcres > 0
    ? Math.round(DEFAULT_BONUS_PER_ACRE * netAcres)
    : 0;
}

/**
 * Builds the default election term set for a freshly-added UMI item: the participate /
 * carried-royalty / lease-royalty defaults, the default risk-penalty trigger + its
 * threshold, ALL the tract's valued wells (the frame is scoped by `wellIds`), and the
 * acreage-seeded lease bonus. Never carries `electedElection` (set later by the
 * Use-in-Evaluation flow, out of scope here).
 *
 * @param {CarryWell[]} wells - The tract's wells, carrying our actual interest in each.
 * @param {number | null} [netAcres] - The tract's net acres, to seed the $/NMA bonus (null ⇒ manual total).
 * @return {ElectionSeed} The election fields to merge onto the item.
 */
export function makeElectionSeed(
  wells: CarryWell[],
  netAcres?: number | null
): ElectionSeed {
  // The participate election IS our actual interest in the group's properties (it is
  // also the APO half of a carried position — see WorkingConfig.ownerWi). Seeded
  // from the certified allocation rather than the mock 25/20 preset (INS-10673).
  const actual = carryTermsFromWells(wells);
  // A tract whose properties all carry ONE interest has exactly one possible
  // grouping, so it is seeded whole and the user never has to assemble the obvious.
  // A tract whose properties DISAGREE is seeded with an EMPTY group instead: there
  // is no single honest participating interest across them, and pre-filling one
  // would price a blend nobody chose. The user splits it, and each group seeds its
  // own terms as it is filled (same rule as an OGL carry position).
  //
  // Keyed on the COHORT count, not on whether terms could be derived: a tract of
  // properties that all carry no certified interest is uniform — one group, blank
  // terms for the user to enter — not a tract in disagreement.
  const oneCohort =
    wells.length > 0 && splitWellsByInterest(wells).length === 1;
  const seeded = oneCohort
    ? makeElectionGroup(
        'Group 1',
        wells.map((well) => well.id),
        actual
      )
    : makeElectionGroup('Group 1');
  // The royalty halves are negotiated terms and keep their defaults; the participate
  // pair is OUR interest, so it lives on the group and is either real or blank.
  const { ownerWi, ownerNri, ...royaltyDefaults } = DEFAULT_ELECTION_TERMS;
  void ownerWi;
  void ownerNri;
  return {
    wellIds: wells.map((well) => well.id),
    electionGroups: [seeded],
    ...royaltyDefaults,
    trigger: DEFAULT_ELECTION_TRIGGER,
    threshold: thresholdForTrigger(DEFAULT_ELECTION_TRIGGER),
    leaseBonus: seedLeaseBonus(netAcres),
  };
}

/**
 * Whether an election is missing our participating interest — the state
 * {@link makeElectionSeed} leaves it in when the tract's wells disagree. Read by the
 * Calculate gate so the tooltip names the real blocker (unentered terms) rather than
 * the downstream one it would otherwise surface ("select an election").
 *
 * @param {InterestItem} item - The interest item.
 * @return {boolean} True for an election whose participate WI/NRI is absent or invalid.
 */
export function electionTermsMissing(item: InterestItem): boolean {
  if (item.kind !== 'election') {
    return false;
  }
  // An EMPTY group prices nothing and is not "missing" anything — it is the state a
  // fresh group opens in. Only a group that HOLDS properties and still has no
  // participating interest is a blocker (mirrors `carryTermsMissing`).
  return electionGroupsOf(item).some(
    (group) =>
      group.wellIds.length > 0 &&
      (!isPct(group.ownerWi) ||
        !isPct(group.ownerNri) ||
        !isPct(group.bpoWi) ||
        !isPct(group.bpoNri))
  );
}

/**
 * Whether a tract's wells disagree on our interest, so the participate terms
 * could NOT be seeded from them. The editor surfaces this — an un-seeded election
 * would otherwise silently show the preset as though it were our position.
 *
 * @param {CarryWell[]} wells - The tract's roster.
 * @return {boolean} True when the wells span more than one rate (or carry none).
 */
export function electionTermsAreAmbiguous(wells: CarryWell[]): boolean {
  return wells.length > 0 && carryTermsFromWells(wells) === null;
}

// ── Completeness gate + terms key (dedup / supersede) ─────────────────────────

const isPct = (value: number | null | undefined): value is number =>
  typeof value === 'number' &&
  Number.isFinite(value) &&
  value >= 0 &&
  value <= 100;

/**
 * Whether an election item is complete enough for the backend to reprice: it has
 * ≥1 well (the frame is scoped by `wellIds`), numeric participate WI/NRI + carried /
 * lease royalty in [0,100], a valid risk-penalty trigger + in-range threshold, and a
 * non-negative lease bonus. Never fire on partial/invalid input (keeps the last-good).
 *
 * @param {InterestItem} item - The interest item.
 * @return {boolean} True when the item can be repriced.
 */
export function isElectionItemComplete(item: InterestItem): boolean {
  if (item.kind !== 'election') {
    return false;
  }
  const groups = electionGroupsOf(item);
  // Every group that holds properties must state our participating interest in them,
  // and at least one group must hold something — an item of empty groups prices
  // nothing. Empty groups are otherwise ignored, so a half-built second group never
  // holds the first one's preview back.
  const filled = groups.filter((group) => group.wellIds.length > 0);
  if (filled.length === 0) {
    return false;
  }
  if (
    filled.some(
      (group) =>
        !isPct(group.ownerWi) ||
        !isPct(group.ownerNri) ||
        !isPct(group.bpoWi) ||
        !isPct(group.bpoNri)
    )
  ) {
    return false;
  }
  // The royalty halves, the trigger and its threshold are negotiated per TRACT — one
  // lease, one risk penalty — so they are checked once, not per group.
  if (!isPct(item.carriedRoyaltyNri) || !isPct(item.leaseRoyaltyNri)) {
    return false;
  }
  if (item.trigger === undefined) {
    return false;
  }
  const meta = CARRY_TRIGGER_BY_ID[item.trigger];
  if (!meta) {
    return false;
  }
  if (meta.hasValue) {
    if (
      typeof item.threshold !== 'number' ||
      !Number.isFinite(item.threshold)
    ) {
      return false;
    }
    if (meta.min !== undefined && item.threshold < meta.min) {
      return false;
    }
    if (meta.max !== undefined && item.threshold > meta.max) {
      return false;
    }
  }
  // The bonus is a real dollar total on the tract's acreage — negative is not a
  // position, it is a typo, and pricing it would quietly invert the lease outcome.
  return (
    typeof item.leaseBonus === 'number' &&
    Number.isFinite(item.leaseBonus) &&
    item.leaseBonus >= 0
  );
}

/**
 * A stable key over an election item's repriceable terms — drives request
 * de-duplication + supersede-in-flight. `electedElection` is EXCLUDED: the reprice
 * computes all three variants regardless, so electing one must not churn a request
 * (it only matters for the committed Case-3 run). Wells are sorted so a
 * reorder that preserves membership doesn't re-fire.
 *
 * @param {InterestItem} item - The interest item.
 * @return {string} The terms key.
 */
export function electionTermsKey(item: InterestItem): string {
  return [
    // Every group's properties + participating interest: a change to any of them
    // reprices the tract, and the response is cached per group.
    electionGroupsOf(item)
      .map((group) =>
        [
          group.id,
          [...group.wellIds].sort().join(','),
          group.bpoWi,
          group.bpoNri,
          group.ownerWi,
          group.ownerNri,
        ].join('|')
      )
      .join(';'),
    item.carriedRoyaltyNri,
    item.trigger,
    item.threshold,
    // The payout basis decides what fills the account and so when the well
    // converts — a repriceable term, not a display preference.
    item.payoutBasis,
    item.leaseRoyaltyNri,
    item.leaseBonus,
  ].join(':');
}

// ── Derivations (cumulative net-to-owner + highest-NPV pick + chart/card model) ─

/**
 * Cumulative net-to-owner by month: running sum of (return − expense) over the
 * variant's own single-leg series.
 *
 * @param {ElectionSeriesPoint[]} series - The variant's monthly series.
 * @return {number[]} The cumulative net-to-owner by month.
 */
export function netToOwnerSeries(series: ElectionSeriesPoint[]): number[] {
  return runningSum(series.map((point) => point.return - point.expense));
}

/**
 * The highest-NPV election (the recommended one, AC — "Highest NPV" tag). Null when
 * there are no results.
 *
 * @param {ElectionResult[]} results - The three variants' reprice outcomes.
 * @return {ElectionVariant | null} The variant with the greatest NPV, or null.
 */
export function highestNpvElection(
  results: ElectionResult[]
): ElectionVariant | null {
  if (results.length === 0) {
    return null;
  }
  return results.reduce((best, result) => {
    return result.npv > best.npv ? result : best;
  }).election;
}

/** One cumulative net-to-owner line to draw (a variant), plus its payout marker month. */
export interface ElectionLine {
  variant: ElectionVariant;
  label: string;
  color: string;
  /** Cumulative net-to-owner aligned to the shared month axis (length === months.length). */
  values: number[];
  /** The variant's payout month on the shared axis (1-based), or null (never reverts). */
  payoutMonth: number | null;
}

/** One outcome card (a variant) — the four metrics + the recommended flag. */
export interface ElectionCard {
  variant: ElectionVariant;
  label: string;
  color: string;
  npv: number;
  netToOwner: number;
  peakExposure: number;
  /** Payout month on the shared axis (1-based), or null. */
  payoutMonth: number | null;
  /** True for the highest-NPV variant (the "Highest NPV" tag). */
  isBest: boolean;
}

/** The whole election comparison model — the chart lines + the outcome cards, on one shared month axis. */
export interface ElectionModel {
  months: number[];
  lines: ElectionLine[];
  cards: ElectionCard[];
  best: ElectionVariant | null;
}

/**
 * Assembles the comparison model from the three variants' reprice results. Each
 * variant's series has its OWN length (a carry that reverts late runs longer than a
 * lease), so a shared month axis is built from the UNION of every variant's dates and
 * each cumulative net-to-owner line is aligned onto it by date (forward-filled) — the
 * carry chart's per-entity-length handling, applied here. Payout ISO dates convert to
 * month indices on that same shared axis so a card's month and its chart marker agree.
 * Variants are emitted in the catalog order (carry / participate / lease).
 *
 * @param {ElectionResult[]} results - The three variants' reprice outcomes (any subset present).
 * @return {ElectionModel} The chart lines + outcome cards on one shared axis.
 */
export function buildElectionModel(results: ElectionResult[]): ElectionModel {
  const byVariant = new Map(results.map((result) => [result.election, result]));
  const ordered = ELECTION_VARIANTS.map((meta) =>
    byVariant.get(meta.id)
  ).filter((result): result is ElectionResult => result !== undefined);

  const axisDates = unionDates(ordered);
  const months = axisDates.map((_, i) => i + 1);
  const monthByDate = new Map(axisDates.map((date, i) => [date, i + 1]));
  const best = highestNpvElection(ordered);

  const lines: ElectionLine[] = [];
  const cards: ElectionCard[] = [];
  for (const result of ordered) {
    const meta = ELECTION_VARIANT_BY_ID[result.election];
    const payoutMonth =
      result.payoutMonth !== null
        ? (monthByDate.get(result.payoutMonth) ?? null)
        : null;
    lines.push({
      variant: result.election,
      label: meta.label,
      color: meta.color,
      values: alignCumulativeToAxis(
        result.series,
        netToOwnerSeries(result.series),
        axisDates
      ),
      payoutMonth,
    });
    cards.push({
      variant: result.election,
      label: meta.label,
      color: meta.color,
      npv: result.npv,
      netToOwner: result.netToOwner,
      peakExposure: result.peakExposure,
      payoutMonth,
      isBest: result.election === best,
    });
  }
  return { months, lines, cards, best };
}

// The sorted union of every variant's series dates — the shared chart x-axis.
function unionDates(results: ElectionResult[]): string[] {
  const dates = new Set<string>();
  for (const result of results) {
    for (const point of result.series) {
      dates.add(point.date);
    }
  }
  return [...dates].sort();
}
